import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MagnifyingGlassIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import {
  DropdownPosition,
  isOutsideAnchors,
  subscribeDropdownPosition,
} from '../../utils/dropdownPortal';
import './Select.scss';

export interface SelectOption {
  value: string;
  label: string;
  displayLabel?: string;
}

export interface SelectGroup {
  label: string;
  options: SelectOption[];
}

export interface SelectProps {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  options?: SelectOption[];
  groups?: SelectGroup[];
  error?: string;
  required?: boolean;
  disabled?: boolean;
  searchable?: boolean;
  fullWidth?: boolean;
  className?: string;
}

export const Select: React.FC<SelectProps> = ({
  label,
  placeholder = 'Select a value',
  value,
  onChange,
  options = [],
  groups = [],
  error,
  required = false,
  disabled = false,
  searchable = true,
  fullWidth = true,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const allOptions = [
    ...options,
    ...groups.flatMap((group) => group.options),
  ];

  const normalizedValue = value != null && value !== '' ? String(value) : '';
  const selectedOption = allOptions.find((opt) => opt.value === normalizedValue);
  const displayValue = selectedOption?.displayLabel ?? selectedOption?.label ?? '';

  const filterOptions = (opts: SelectOption[]) => {
    if (!searchTerm) return opts;
    const term = searchTerm.toLowerCase();
    return opts.filter((opt) => opt.label.toLowerCase().includes(term));
  };

  const filteredOptions = filterOptions(options);
  const filteredGroups = groups
    .map((group) => ({
      ...group,
      options: filterOptions(group.options),
    }))
    .filter((group) => group.options.length > 0);

  const hasResults = filteredOptions.length > 0 || filteredGroups.length > 0;

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchTerm('');
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOutsideAnchors(event.target as Node, [
          containerRef.current,
          dropdownRef.current,
        ])
      ) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeDropdown]);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setDropdownPosition(null);
      return;
    }

    return subscribeDropdownPosition(triggerRef.current, setDropdownPosition);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen((open) => {
        if (open) {
          setSearchTerm('');
          return false;
        }
        setSearchTerm('');
        return true;
      });
    }
  };

  const handleSelect = (optionValue: string) => {
    onChange?.(optionValue);
    closeDropdown();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeDropdown();
    }
  };

  const wrapperClasses = [
    'select-wrapper',
    fullWidth && 'select-wrapper--full-width',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const triggerClasses = [
    'select-trigger',
    isOpen && 'select-trigger--open',
    error && 'select-trigger--error',
    disabled && 'select-trigger--disabled',
  ]
    .filter(Boolean)
    .join(' ');

  const dropdown =
    isOpen && dropdownPosition
      ? createPortal(
          <div
            ref={dropdownRef}
            className={`select-dropdown select-dropdown--portal select-dropdown--${dropdownPosition.placement}`}
            style={{
              top: dropdownPosition.top,
              bottom: dropdownPosition.bottom,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              maxHeight: dropdownPosition.maxHeight,
            }}
          >
            {searchable && (
              <div className="select-search">
                <MagnifyingGlassIcon className="select-search-icon" />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="select-search-input"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            )}

            <div className="select-options" role="listbox">
              {!hasResults && (
                <div className="select-no-results">No options found</div>
              )}

              {filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`select-option ${normalizedValue === option.value ? 'select-option--selected' : ''}`}
                  onClick={() => handleSelect(option.value)}
                  role="option"
                  aria-selected={normalizedValue === option.value}
                >
                  {option.label}
                </div>
              ))}

              {filteredGroups.map((group) => (
                <div key={group.label} className="select-group">
                  <div className="select-group-label">{group.label}</div>
                  {group.options.map((option) => (
                    <div
                      key={option.value}
                      className={`select-option select-option--grouped ${normalizedValue === option.value ? 'select-option--selected' : ''}`}
                      onClick={() => handleSelect(option.value)}
                      role="option"
                      aria-selected={normalizedValue === option.value}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className={wrapperClasses} ref={containerRef} onKeyDown={handleKeyDown}>
      {label && (
        <label className="select-label">
          {label}
          {required && <span className="select-required">*</span>}
        </label>
      )}

      <button
        ref={triggerRef}
        type="button"
        className={triggerClasses}
        onClick={handleToggle}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={`select-value ${!displayValue ? 'select-value--placeholder' : ''}`}>
          {displayValue || placeholder}
        </span>
        <ChevronDownIcon className={`select-chevron ${isOpen ? 'select-chevron--open' : ''}`} />
      </button>

      {dropdown}

      {error && <span className="select-error">{error}</span>}
    </div>
  );
};
